<template>
  <div class="header-wrapper">
  <v-app-bar
  
    height="80"
    app
    color="white"
    elevate-on-scroll
    flat
    class="modern-header"
  >
    <!-- Left: Logo -->
    <v-toolbar-title class="logo logo-padding" @click="goToHome">
      <v-img
        src="@/assets/icons/OhYesMainLogo.png"
        alt="OH.YES Logo"
        contain
        height="52"
        width="140"
      />
    </v-toolbar-title>

    <v-spacer />

    <!-- ================= DESKTOP NAV ================= -->
    <div class="d-none d-md-flex nav-links">
      <router-link
        v-for="item in menuItems"
        :key="item.title"
        :to="item.path"
        class="nav-link"
        exact
        :class="{ active: $route.path === item.path }"
      >
        {{ item.title }}
      </router-link>

      <v-btn
        small
        class="partner-register-btn"
        depressed
        @click="openPartnerRegistration"
      >
        <v-icon left small class="partner-btn-icon">mdi-account-plus</v-icon>
        Partner Registration
      </v-btn>

      <!-- Desktop Account -->
      <div v-if="isLoggedIn" class="account-section">
        <v-btn
          text
          small
          color="#000"
          class="my-bookings-btn mx-2"
          @click="$router.push(bookingsPath)"
        >
          <v-icon left small>mdi-calendar-text</v-icon>
          My Bookings
        </v-btn>

        <v-menu offset-y bottom>
          <template v-slot:activator="{ on, attrs }">
            <div class="account-trigger ml-2" v-bind="attrs" v-on="on">
              <v-icon size="22">mdi-account-circle</v-icon>
              <span class="user-mobile-text">{{ maskedMobileNumber }}</span>
              <v-icon size="18">mdi-chevron-down</v-icon>
            </div>
          </template>

          <v-card class="pa-2 d-flex flex-column" min-width="150">
            <v-chip
              small
              outlined
              color="red"
              class="logout-chip justify-center"
              @click="logout"
            >
              <v-icon left small>mdi-logout</v-icon>
              Logout
            </v-chip>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- ================= MOBILE ACCOUNT ICON ================= -->
    <div v-if="isLoggedIn" class="d-flex d-md-none mr-2 align-center">
      <v-btn
        icon
        small
        color="#000"
        class="mr-2"
        @click="$router.push(bookingsPath)"
      >
        <v-icon size="20">mdi-calendar-text</v-icon>
      </v-btn>

      <v-menu offset-y bottom>
        <template v-slot:activator="{ on, attrs }">
          <div class="mobile-account-trigger" v-bind="attrs" v-on="on">
            <v-icon size="24" color="#000">mdi-account-circle</v-icon>
            <span class="user-mobile-text mobile-only">{{ maskedMobileNumber }}</span>
          </div>
        </template>

        <v-card class="pa-2 d-flex flex-column" min-width="120">
          <v-chip
            small
            outlined
            color="red"
            class="logout-chip justify-center"
            @click="logout"
          >
            <v-icon left small>mdi-logout</v-icon>
            Logout
          </v-chip>
        </v-card>
      </v-menu>
    </div>

    <!-- Mobile Menu Icon -->
    <v-app-bar-nav-icon
      class="d-md-none"
      color="#000"
      @click.stop="drawer = true"
    />
  </v-app-bar>

  <!-- Custom Mobile Menu (replaces Vuetify drawer - no blur) -->
  <transition name="mobile-menu">
    <div v-if="drawer" class="mobile-menu-overlay d-md-none" @click="drawer = false">
      <div class="mobile-menu-panel" @click.stop>
        <div class="mobile-menu-header">
          <span class="mobile-menu-title">Menu</span>
          <button type="button" class="mobile-menu-close" @click="drawer = false" aria-label="Close menu">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <div class="mobile-menu-divider"></div>
        <nav class="mobile-menu-nav">
          <router-link
            v-for="item in menuItems"
            :key="item.title"
            :to="item.path"
            exact
            class="mobile-menu-link"
            @click.native="closeDrawerOnNav"
          >
            {{ item.title }}
          </router-link>
          <div
            v-if="isLoggedIn"
            class="mobile-menu-link"
            @click="$router.push(bookingsPath); drawer = false"
          >
            <v-icon small class="mr-2">mdi-calendar-text</v-icon>
            My Bookings
          </div>
          <v-btn
            block
            class="partner-register-btn mt-3"
            depressed
            @click="openPartnerRegistrationFromDrawer"
          >
            <v-icon left small class="partner-btn-icon">mdi-account-plus</v-icon>
            Partner Registration
          </v-btn>
        </nav>
      </div>
    </div>
  </transition>

  <!-- Partner app store choice modal -->
  <v-dialog
    v-model="partnerStoreModal"
    max-width="380"
    persistent
    content-class="partner-store-dialog"
    @keydown.esc="partnerStoreModal = false"
  >
    <v-card class="partner-store-card" elevation="0">
      <div class="partner-store-header">
        <div class="partner-store-header-icon">
          <v-icon size="32" color="white">mdi-account-plus</v-icon>
        </div>
        <h2 class="partner-store-title">Get the Partner App</h2>
        <p class="partner-store-subtitle">Join our professionals & manage bookings on the go</p>
      </div>
      <v-card-text class="partner-store-body">
        <span class="partner-store-label">Download from your store</span>
        <a
          href="https://apps.apple.com/in/app/oh-yes-partner/id6472038987"
          target="_blank"
          rel="noopener noreferrer"
          class="partner-store-link partner-store-link-apple"
          @click="partnerStoreModal = false"
        >
          <v-icon class="partner-store-link-icon">mdi-apple</v-icon>
          <span>App Store</span>
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.ohyes.partner"
          target="_blank"
          rel="noopener noreferrer"
          class="partner-store-link partner-store-link-google"
          @click="partnerStoreModal = false"
        >
          <v-icon class="partner-store-link-icon">mdi-google-play</v-icon>
          <span>Google Play</span>
        </a>
      </v-card-text>
      <v-card-actions class="partner-store-footer">
        <v-btn text small class="partner-store-cancel" @click="partnerStoreModal = false">
          Maybe later
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
export default {
  name: "HeaderPage",

  data() {
    return {
      drawer: false,
      authTrigger: 0,
      partnerStoreModal: false,
    };
  },

  computed: {
    isLoggedIn() {
      // eslint-disable-next-line no-unused-expressions
      this.authTrigger;
      return !!localStorage.getItem("access_token");
    },

    maskedMobileNumber() {
      // eslint-disable-next-line no-unused-expressions
      this.authTrigger;
      const number = localStorage.getItem("mobile_number");
      if (!number) return "";
      
      if (number.length <= 4) return number;
      
      return (
        number.slice(0, 2) + 
        "*".repeat(Math.max(1, number.length - 4)) + 
        number.slice(-2)
      );
    },

    isUS() {
      return (
        this.$route.meta?.location === "us" ||
        localStorage.getItem("isUS") === "true"
      );
    },

    basePath() {
      return this.isUS ? "/us" : "";
    },

    bookingsPath() {
      return this.isUS ? "/us/bookings" : "/bookings";
    },

    menuItems() {
      const items = [
        { title: "Home", path: this.isUS ? "/us" : "/" },
        { title: "About", path: `${this.basePath}/about` },
        { title: "Services", path: `${this.basePath}/services` },
        { title: "Blog", path: `${this.basePath}/blog` },
      ];

      if (!this.isUS) {
        items.push({ title: "News", path: "/news" });
      }

      return items;
    },
  },

  watch: {
    $route() {
      this.drawer = false;
    },
    drawer(isOpen) {
      if (typeof document !== "undefined") {
        document.body.style.overflow = isOpen ? "hidden" : "";
      }
    },
  },

  mounted() {
    this.$bus.$on("auth-change", () => {
      this.authTrigger++;
    });
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    this.$bus.$off("auth-change");
    window.removeEventListener("resize", this.handleResize);
  },

  methods: {
    openPartnerRegistration() {
      this.partnerStoreModal = true;
    },

    openPartnerRegistrationFromDrawer() {
      this.drawer = false;
      this.$nextTick(() => {
        this.partnerStoreModal = true;
      });
    },

    goToHome() {
      this.$router.push(this.isUS ? "/us" : "/");
    },

    closeDrawerOnNav() {
      this.$nextTick(() => (this.drawer = false));
    },

    handleResize() {
      if (window.innerWidth >= 960 && this.drawer) {
        this.drawer = false;
      }
    },

    logout() {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("mobile_number");
      localStorage.removeItem("is_registered");
      localStorage.removeItem("authRegion");

      this.$bus.$emit("auth-change");
      this.$router.push(this.isUS ? "/us" : "/");
    },
  },
};
</script>

<style scoped>
.modern-header {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 5 !important;
}

/* Disable backdrop-filter on mobile - causes blurry/broken rendering on iOS Safari */
@media (max-width: 960px) {
  .modern-header {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}

.logo {
  cursor: pointer;
}

.logo-padding {
  padding-left: 50px;
}

@media (max-width: 960px) {
  .logo-padding {
    padding-left: 16px;
  }
}

.nav-links {
  display: flex;
  gap: 40px;
  align-items: center;
}

.nav-link {
  font-size: 15px;
  color: #000;
  text-decoration: none;
  font-weight: 700;
  position: relative;
  transition: opacity 0.25s ease;
}

.nav-link:hover {
  opacity: 0.7;
}

.nav-link.active::after {
  content: "";
  position: absolute;
  bottom: -6px;
  left: 0;
  right: 0;
  height: 2px;
  background: #ff6b35;
}

.partner-register-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  margin-left: -10px;
  color: #fff !important;
  background: linear-gradient(135deg, #ff5f1f 0%, #ff7a2f 45%, #ff9a45 100%);
  box-shadow: 0 8px 18px rgba(255, 107, 53, 0.24) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.partner-register-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(255, 107, 53, 0.3) !important;
}

.partner-btn-icon {
  color: #fff !important;
}

@media (max-width: 960px) {
  .partner-register-btn {
    margin-left: 0;
  }
}

.drawer-link {
  text-decoration: none;
  display: block;
}

.account-section {
  display: flex;
  align-items: center;
  margin-left: 20px;
  border-left: 1px solid rgba(0,0,0,0.1);
  padding-left: 10px;
}

.account-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-weight: 600;
  color: #000;
  padding: 4px 8px;
  border-radius: 20px;
  transition: background 0.2s;
}

.account-trigger:hover {
  background: rgba(0, 0, 0, 0.05);
}

.my-bookings-btn {
  font-weight: 700 !important;
  text-transform: none !important;
}

.user-mobile-text {
  font-size: 14px;
  font-weight: 600;
  color: #000;
}

.mobile-account-trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

@media (max-width: 600px) {
  .user-mobile-text.mobile-only {
    font-size: 12px;
  }
}

.logout-chip {
  cursor: pointer;
}

/* ================= CUSTOM MOBILE MENU (no blur) ================= */
.header-wrapper {
  position: relative;
}

.mobile-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
}

.mobile-menu-panel {
  width: 280px;
  max-width: 85vw;
  height: 100%;
  background: #fff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  padding: 16px 0;
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 12px;
}

.mobile-menu-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #000;
}

.mobile-menu-close {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.12);
  margin: 0 16px;
}

.mobile-menu-nav {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-menu-link {
  padding: 12px 16px;
  color: #000;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
}

.mobile-menu-link:hover {
  background: rgba(0, 0, 0, 0.05);
}

.mobile-menu-link.router-link-active {
  background: rgba(255, 107, 53, 0.1);
  color: #ff6b35;
}

/* Slide-in transition */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.25s ease;
}

.mobile-menu-enter-active .mobile-menu-panel,
.mobile-menu-leave-active .mobile-menu-panel {
  transition: transform 0.25s ease;
}

.mobile-menu-enter,
.mobile-menu-leave-to {
  opacity: 0;
}

.mobile-menu-enter .mobile-menu-panel,
.mobile-menu-leave-to .mobile-menu-panel {
  transform: translateX(100%);
}
</style>

<style lang="scss">
/* Unscoped so dialog overlay (portaled to body) is styled */
.partner-store-dialog .v-dialog {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18), 0 8px 16px rgba(255, 95, 31, 0.12) !important;
}
.partner-store-dialog .partner-store-card {
  border-radius: 20px;
  overflow: hidden;
  padding: 0;
}
.partner-store-dialog .partner-store-header {
  background: linear-gradient(135deg, #ff5f1f 0%, #ff7a2f 45%, #ff9a45 100%);
  padding: 28px 24px 24px;
  text-align: center;
}
.partner-store-dialog .partner-store-header-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
}
.partner-store-dialog .partner-store-title {
  font-weight: 700;
  font-size: 1.35rem;
  color: #fff;
  margin: 0 0 6px;
  letter-spacing: -0.02em;
  line-height: 1.25;
}
.partner-store-dialog .partner-store-subtitle {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
  font-weight: 500;
  line-height: 1.35;
}
.partner-store-dialog .partner-store-body {
  padding: 24px !important;
}
.partner-store-dialog .partner-store-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 14px;
}
.partner-store-dialog .partner-store-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border-radius: 14px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.05rem;
  text-transform: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.partner-store-dialog .partner-store-link:last-of-type {
  margin-bottom: 0;
}
.partner-store-dialog .partner-store-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  opacity: 0.95;
}
.partner-store-dialog .partner-store-link:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.partner-store-dialog .partner-store-link-icon {
  font-size: 26px;
  color: inherit;
}
.partner-store-dialog .partner-store-link-apple {
  background: linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%);
  color: #1d1d1f;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.partner-store-dialog .partner-store-link-apple:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}
.partner-store-dialog .partner-store-link-google {
  background: linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #1b5e20;
  border: 1px solid rgba(27, 94, 32, 0.2);
}
.partner-store-dialog .partner-store-link-google:hover {
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.25);
}
.partner-store-dialog .partner-store-footer {
  padding: 12px 24px 20px !important;
  justify-content: center;
}
.partner-store-dialog .partner-store-cancel {
  color: rgba(0, 0, 0, 0.5) !important;
  text-transform: none;
  font-weight: 500;
  font-size: 0.9rem;
}
.partner-store-dialog .partner-store-cancel:hover {
  color: rgba(0, 0, 0, 0.75) !important;
  background: transparent !important;
}
</style>
